import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StudentManagement() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [summary, setSummary] = useState({ active: 0, trial: 0, cancelled: 0, expired: 0, total: 0 });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [message, setMessage] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter !== "all") params.append("status", statusFilter);
      params.append("page", page);
      params.append("limit", 20);

      const res = await axios.get(`${API}/admin/students?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });

      if (res.data.success) {
        setStudents(res.data.students);
        setPagination(res.data.pagination);
        setSummary(res.data.summary);
      }
    } catch (err) {
      console.error("Error fetching students:", err);
      const status = err.response?.status;
      let errorMsg = "Error loading students: ";
      
      if (status === 503) {
        errorMsg += "Server temporarily unavailable. Please try again in a moment.";
      } else if (status === 401 || status === 403) {
        errorMsg += "Session expired. Please logout and login again.";
      } else if (err.code === 'ECONNABORTED') {
        errorMsg += "Request timed out. Please try again.";
      } else {
        errorMsg += (err.response?.data?.detail || err.message);
      }
      setMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchStudents();
  };

  const handleSubscriptionAction = async (studentId, action, days = 7) => {
    setActionLoading(true);
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(
        `${API}/admin/students/${studentId}/subscription?action=${action}&days=${days}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(res.data.message);
      fetchStudents();
      setSelectedStudent(null);
    } catch (err) {
      setMessage("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnlockAccount = async (email) => {
    setActionLoading(true);
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/admin/unlock-account?identifier=${encodeURIComponent(email)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(`✓ ${res.data.message}`);
      fetchStudents();
    } catch (err) {
      setMessage("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPassword = async (email) => {
    const newPassword = window.prompt("Enter new password (leave blank to auto-generate):");
    if (newPassword === null) return; // User cancelled
    
    setActionLoading(true);
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      const url = newPassword 
        ? `${API}/admin/set-password?identifier=${encodeURIComponent(email)}&new_password=${encodeURIComponent(newPassword)}`
        : `${API}/admin/set-password?identifier=${encodeURIComponent(email)}`;
      
      const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      
      // Show the new password to the admin
      alert(`Password set successfully!\n\nUsername: ${res.data.username}\nNew Password: ${res.data.new_password}\n\nGive this password to the user.`);
      setMessage(`✓ Password set for ${res.data.username}. Account unlocked.`);
      fetchStudents();
      setSelectedStudent(null);
    } catch (err) {
      setMessage("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteStudent = async (studentId, username) => {
    if (!window.confirm(`Are you sure you want to DELETE ${username}? This cannot be undone.`)) {
      return;
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.delete(`${API}/admin/students/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage(res.data.message);
      fetchStudents();
      setSelectedStudent(null);
    } catch (err) {
      setMessage("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: "#1a4d1a", color: "#90EE90", text: "Active" },
      trial: { bg: "#1a3a4d", color: "#87CEEB", text: "Trial" },
      cancelled: { bg: "#4d4d1a", color: "#FFD700", text: "Cancelled" },
      expired: { bg: "#4d1a1a", color: "#FF6B6B", text: "Expired" },
      inactive: { bg: "#4d1a1a", color: "#FF6B6B", text: "Inactive" }
    };
    const style = styles[status] || styles.inactive;
    return (
      <span style={{
        background: style.bg,
        color: style.color,
        padding: "4px 10px",
        borderRadius: "12px",
        fontSize: "0.75rem",
        fontWeight: "bold"
      }}>
        {style.text}
      </span>
    );
  };

  return (
    <div className="card" data-testid="student-management">
      <h2 style={{ color: "#FFD700", marginBottom: "20px" }}>
        Student Management
      </h2>

      {/* Summary Cards */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", 
        gap: "10px", 
        marginBottom: "25px" 
      }}>
        <div 
          onClick={() => { setStatusFilter("all"); setPage(1); }}
          style={{ 
            background: statusFilter === "all" ? "#333" : "#222", 
            padding: "15px", 
            borderRadius: "8px", 
            textAlign: "center",
            cursor: "pointer",
            border: statusFilter === "all" ? "2px solid #FFD700" : "2px solid transparent"
          }}
        >
          <div style={{ color: "#FFD700", fontSize: "1.5rem", fontWeight: "bold" }}>{summary.total}</div>
          <div style={{ color: "#999", fontSize: "0.8rem" }}>Total</div>
        </div>
        <div 
          onClick={() => { setStatusFilter("active"); setPage(1); }}
          style={{ 
            background: statusFilter === "active" ? "#1a4d1a" : "#222", 
            padding: "15px", 
            borderRadius: "8px", 
            textAlign: "center",
            cursor: "pointer",
            border: statusFilter === "active" ? "2px solid #90EE90" : "2px solid transparent"
          }}
        >
          <div style={{ color: "#90EE90", fontSize: "1.5rem", fontWeight: "bold" }}>{summary.active}</div>
          <div style={{ color: "#999", fontSize: "0.8rem" }}>Active</div>
        </div>
        <div 
          onClick={() => { setStatusFilter("trial"); setPage(1); }}
          style={{ 
            background: statusFilter === "trial" ? "#1a3a4d" : "#222", 
            padding: "15px", 
            borderRadius: "8px", 
            textAlign: "center",
            cursor: "pointer",
            border: statusFilter === "trial" ? "2px solid #87CEEB" : "2px solid transparent"
          }}
        >
          <div style={{ color: "#87CEEB", fontSize: "1.5rem", fontWeight: "bold" }}>{summary.trial}</div>
          <div style={{ color: "#999", fontSize: "0.8rem" }}>Trial</div>
        </div>
        <div 
          onClick={() => { setStatusFilter("cancelled"); setPage(1); }}
          style={{ 
            background: statusFilter === "cancelled" ? "#4d4d1a" : "#222", 
            padding: "15px", 
            borderRadius: "8px", 
            textAlign: "center",
            cursor: "pointer",
            border: statusFilter === "cancelled" ? "2px solid #FFD700" : "2px solid transparent"
          }}
        >
          <div style={{ color: "#FFD700", fontSize: "1.5rem", fontWeight: "bold" }}>{summary.cancelled}</div>
          <div style={{ color: "#999", fontSize: "0.8rem" }}>Cancelled</div>
        </div>
        <div 
          onClick={() => { setStatusFilter("expired"); setPage(1); }}
          style={{ 
            background: statusFilter === "expired" ? "#4d1a1a" : "#222", 
            padding: "15px", 
            borderRadius: "8px", 
            textAlign: "center",
            cursor: "pointer",
            border: statusFilter === "expired" ? "2px solid #FF6B6B" : "2px solid transparent"
          }}
        >
          <div style={{ color: "#FF6B6B", fontSize: "1.5rem", fontWeight: "bold" }}>{summary.expired}</div>
          <div style={{ color: "#999", fontSize: "0.8rem" }}>Expired</div>
        </div>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} style={{ marginBottom: "20px" }}>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Search by username or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: "200px" }}
            data-testid="student-search-input"
          />
          <button type="submit" style={{ marginTop: 0 }} data-testid="student-search-btn">
            Search
          </button>
          {search && (
            <button 
              type="button" 
              onClick={() => { setSearch(""); setPage(1); }}
              style={{ marginTop: 0, background: "#333", color: "#EEEEEE" }}
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {/* Message */}
      {message && (
        <div style={{
          padding: "12px",
          background: message.includes("Error") ? "#4d1a1a" : "#1a4d1a",
          color: message.includes("Error") ? "#FF6B6B" : "#90EE90",
          borderRadius: "6px",
          marginBottom: "15px"
        }}>
          {message}
        </div>
      )}

      {/* Students Table */}
      {loading ? (
        <p style={{ color: "#999", textAlign: "center", padding: "30px" }}>Loading students...</p>
      ) : students.length === 0 ? (
        <p style={{ color: "#999", textAlign: "center", padding: "30px" }}>No students found.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#222" }}>
                <th style={{ padding: "12px", textAlign: "left", color: "#FFD700", borderBottom: "2px solid #333" }}>
                  Username
                </th>
                <th style={{ padding: "12px", textAlign: "left", color: "#FFD700", borderBottom: "2px solid #333" }}>
                  Email
                </th>
                <th style={{ padding: "12px", textAlign: "center", color: "#FFD700", borderBottom: "2px solid #333" }}>
                  Status
                </th>
                <th style={{ padding: "12px", textAlign: "center", color: "#FFD700", borderBottom: "2px solid #333" }}>
                  Trial Days
                </th>
                <th style={{ padding: "12px", textAlign: "center", color: "#FFD700", borderBottom: "2px solid #333" }}>
                  Points
                </th>
                <th style={{ padding: "12px", textAlign: "center", color: "#FFD700", borderBottom: "2px solid #333" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {students.map((student, idx) => (
                <tr 
                  key={student.id} 
                  style={{ 
                    background: idx % 2 === 0 ? "#1a1a1a" : "#222",
                    cursor: "pointer"
                  }}
                  onClick={() => setSelectedStudent(student)}
                >
                  <td style={{ padding: "12px", color: "#EEEEEE", borderBottom: "1px solid #333" }}>
                    {student.username}
                    {student.ordained && <span style={{ marginLeft: "8px", color: "#FFD700" }}>⭐</span>}
                  </td>
                  <td style={{ padding: "12px", color: "#87CEEB", borderBottom: "1px solid #333", fontSize: "0.9rem" }}>
                    {student.email}
                  </td>
                  <td style={{ padding: "12px", textAlign: "center", borderBottom: "1px solid #333" }}>
                    {getStatusBadge(student.subscription_status)}
                  </td>
                  <td style={{ padding: "12px", textAlign: "center", color: "#CCCCCC", borderBottom: "1px solid #333" }}>
                    {student.trial_days_remaining !== undefined ? (
                      <span style={{ color: student.trial_days_remaining <= 0 ? "#FF6B6B" : "#90EE90" }}>
                        {student.trial_days_remaining} days
                      </span>
                    ) : "-"}
                  </td>
                  <td style={{ padding: "12px", textAlign: "center", color: "#FFD700", borderBottom: "1px solid #333" }}>
                    {student.points || 0}
                  </td>
                  <td style={{ padding: "12px", textAlign: "center", borderBottom: "1px solid #333" }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedStudent(student); }}
                      style={{
                        padding: "6px 12px",
                        background: "#333",
                        color: "#87CEEB",
                        fontSize: "0.8rem",
                        marginTop: 0
                      }}
                    >
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "20px" }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ marginTop: 0, background: "#333", color: page === 1 ? "#666" : "#EEEEEE" }}
          >
            Previous
          </button>
          <span style={{ color: "#999", padding: "10px" }}>
            Page {page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
            disabled={page === pagination.pages}
            style={{ marginTop: 0, background: "#333", color: page === pagination.pages ? "#666" : "#EEEEEE" }}
          >
            Next
          </button>
        </div>
      )}

      {/* Student Detail Modal */}
      {selectedStudent && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px"
          }}
          onClick={() => setSelectedStudent(null)}
        >
          <div 
            style={{
              background: "#1a1a1a",
              borderRadius: "12px",
              padding: "25px",
              maxWidth: "500px",
              width: "100%",
              maxHeight: "80vh",
              overflowY: "auto",
              border: "2px solid #FFD700"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "20px" }}>
              <div>
                <h3 style={{ color: "#FFD700", margin: 0 }}>{selectedStudent.username}</h3>
                <p style={{ color: "#87CEEB", margin: "5px 0 0", fontSize: "0.9rem" }}>{selectedStudent.email}</p>
              </div>
              <button
                onClick={() => setSelectedStudent(null)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#999",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  padding: 0,
                  marginTop: 0
                }}
              >
                ×
              </button>
            </div>

            <div style={{ background: "#222", padding: "15px", borderRadius: "8px", marginBottom: "20px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px" }}>
                <div>
                  <p style={{ color: "#999", fontSize: "0.8rem", margin: 0 }}>Status</p>
                  <p style={{ color: "#EEEEEE", margin: "5px 0 0" }}>{getStatusBadge(selectedStudent.subscription_status)}</p>
                </div>
                <div>
                  <p style={{ color: "#999", fontSize: "0.8rem", margin: 0 }}>Points</p>
                  <p style={{ color: "#FFD700", margin: "5px 0 0", fontWeight: "bold" }}>{selectedStudent.points || 0}</p>
                </div>
                <div>
                  <p style={{ color: "#999", fontSize: "0.8rem", margin: 0 }}>Quizzes Completed</p>
                  <p style={{ color: "#EEEEEE", margin: "5px 0 0" }}>{selectedStudent.total_quizzes || 0}</p>
                </div>
                <div>
                  <p style={{ color: "#999", fontSize: "0.8rem", margin: 0 }}>Trial Days Left</p>
                  <p style={{ 
                    color: selectedStudent.trial_days_remaining <= 0 ? "#FF6B6B" : "#90EE90", 
                    margin: "5px 0 0" 
                  }}>
                    {selectedStudent.trial_days_remaining !== undefined ? `${selectedStudent.trial_days_remaining} days` : "N/A"}
                  </p>
                </div>
                {selectedStudent.ordained && (
                  <div style={{ gridColumn: "span 2" }}>
                    <p style={{ color: "#FFD700", margin: 0 }}>⭐ Ordained Minister</p>
                  </div>
                )}
              </div>
            </div>

            <h4 style={{ color: "#87CEEB", marginBottom: "15px" }}>Subscription Actions</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {selectedStudent.subscription_status !== "active" && (
                <button
                  onClick={() => handleSubscriptionAction(selectedStudent.id, "activate")}
                  disabled={actionLoading}
                  style={{ background: "#1a4d1a", color: "#90EE90", marginTop: 0 }}
                >
                  Activate Subscription
                </button>
              )}
              {selectedStudent.subscription_status === "active" && (
                <button
                  onClick={() => handleSubscriptionAction(selectedStudent.id, "cancel")}
                  disabled={actionLoading}
                  style={{ background: "#4d4d1a", color: "#FFD700", marginTop: 0 }}
                >
                  Cancel Subscription
                </button>
              )}
              <button
                onClick={() => handleSubscriptionAction(selectedStudent.id, "extend_trial", 7)}
                disabled={actionLoading}
                style={{ background: "#1a3a4d", color: "#87CEEB", marginTop: 0 }}
              >
                Extend Trial (+7 Days)
              </button>
              <button
                onClick={() => handleSubscriptionAction(selectedStudent.id, "reactivate")}
                disabled={actionLoading}
                style={{ background: "#333", color: "#EEEEEE", marginTop: 0 }}
              >
                Reactivate (New 7-Day Trial)
              </button>
              
              <hr style={{ border: "none", borderTop: "1px solid #333", margin: "10px 0" }} />
              <h4 style={{ color: "#FF6B6B", marginBottom: "10px", marginTop: "5px" }}>Account Control</h4>
              
              <button
                onClick={() => handleUnlockAccount(selectedStudent.email)}
                disabled={actionLoading}
                style={{ background: "#3a3a1a", color: "#FFD700", marginTop: 0 }}
              >
                🔓 Unlock Account (Clear Lockouts)
              </button>
              <button
                onClick={() => handleSetPassword(selectedStudent.email)}
                disabled={actionLoading}
                style={{ background: "#1a2a3a", color: "#87CEEB", marginTop: 0 }}
              >
                🔑 Set New Password
              </button>
              
              <hr style={{ border: "none", borderTop: "1px solid #333", margin: "10px 0" }} />
              <button
                onClick={() => handleDeleteStudent(selectedStudent.id, selectedStudent.username)}
                disabled={actionLoading}
                style={{ background: "#4d1a1a", color: "#FF6B6B", marginTop: 0 }}
              >
                ⚠️ Delete Student Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentManagement;
